import {
  initClientAnalytics,
  attachConsoleApi,
  type BootstrapData,
  type EdgeFlags,
} from '@listening/frontend/analytics/client';
import { ClientFlags } from '@listening/frontend/analytics/flags/client';
import { clientEnv } from '@/env';

let initialized = false;

/**
 * Init PostHog once on the client with bootstrap data from route context.
 *
 * Called during RootComponent render (before children mount) so flags
 * are available synchronously to the entire tree. Server functions +
 * route context handle the SSR data handoff — no window globals needed.
 *
 * @see https://posthog.com/docs/feature-flags/bootstrapping
 */
export function initPostHogFromRouteContext(
  bootstrap: BootstrapData | undefined,
  edgeFlags: EdgeFlags | undefined,
) {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // Synthetic traffic (user-journey canary, Playwright e2e) sets
  // window.IS_TEST before any navigation. Skip PostHog entirely so it emits
  // no $pageview, no $feature_flag_called exposures, and no conversion
  // events — the canary pins itself to every edge experiment's control arm
  // (see CANARY_LIS_BUCKET_ID), so its events would one-sidedly inflate
  // control's exposure and conversion counts. Uninitialized posthog-js calls
  // are documented no-ops throughout this codebase, and flag reads stay
  // functional: edge variants come from EdgeFlagsContext (SSR route
  // context), and everything else falls back to its default — the most
  // stable configuration for a canary. Nothing waits on a flag load that can
  // never happen: the paywall's arm comes from the API, not from PostHog.
  if (window.IS_TEST === true) return;

  // Off outside staging/production — see `analyticsEnabledConfig`.
  if (!clientEnv.analyticsEnabled) return;

  initClientAnalytics({
    posthogKey: clientEnv.posthogPublicKey,
    // Same-origin ingest proxy on our own CloudFront (applyPosthogIngestCdnTransform
    // in packages/infra/src/web). On localstack this resolves to posthogHost, so the two
    // are equal and ui_host is a no-op.
    posthogHost: clientEnv.posthogIngestHost,
    posthogUiHost: clientEnv.posthogHost,
    defaults: '2026-06-25',
    bootstrap: bootstrap ?? undefined,
    edgeFlags: edgeFlags ?? undefined,
  });

  attachConsoleApi(ClientFlags);
}
